/**
 * Typed API Client for NovelReader Admin
 * Handles all communication with the admin-worker API
 */
import type { Novel, NovelStatus, Chapter, Author } from "@novelreader/types";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

// ---------------------------------------------------------------------------
// Error Types
// ---------------------------------------------------------------------------

export interface ValidationError {
  field: string;
  message: string;
}

export interface ApiErrorResponse {
  error: string;
  message?: string;
  errors?: ValidationError[];
}

/**
 * Custom error class for API errors with status code and validation errors
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly errors?: ValidationError[];

  constructor(message: string, status: number, errors?: ValidationError[]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

// ---------------------------------------------------------------------------
// Base Request Function
// ---------------------------------------------------------------------------

/**
 * Make an authenticated API request
 * Handles common error cases:
 * - 401: Redirect to login
 * - 422: Return validation errors
 * - 500: Throw with error message
 */
async function apiRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;

  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    // Handle 401 - redirect to login
    if (response.status === 401) {
      window.location.href = "/login";
      throw new ApiError("Unauthorized", 401);
    }

    // Parse error response
    let errorData: ApiErrorResponse;
    try {
      errorData = await response.json();
    } catch {
      throw new ApiError("Request failed", response.status);
    }

    // Handle validation errors (422)
    if (response.status === 422 && errorData.errors) {
      throw new ApiError(
        errorData.error || "Validation failed",
        422,
        errorData.errors,
      );
    }

    // Handle other errors
    throw new ApiError(
      errorData.error || errorData.message || `HTTP ${response.status}`,
      response.status,
    );
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Request/Response Types
// ---------------------------------------------------------------------------

// Novel types
export interface CreateNovelRequest {
  title: string;
  description?: string;
  coverUrl?: string;
  status?: NovelStatus;
}

export interface UpdateNovelRequest {
  title?: string;
  description?: string | null;
  coverUrl?: string | null;
  status?: NovelStatus;
}

export interface NovelListResponse {
  novels: Novel[];
}

export interface NovelResponse {
  novel: Novel;
}

export interface DeleteResponse {
  success: boolean;
}

// Chapter types (without r2Key for client-side)
export type ChapterListItem = Omit<Chapter, "content" | "r2Key">;

export interface CreateChapterRequest {
  title: string;
  content: string;
  tierId?: string | null; // NULL = free access
  publishedAt?: string | null;
}

export interface UpdateChapterRequest {
  title?: string;
  content?: string;
  tierId?: string | null; // NULL = free access
  publishedAt?: string | null;
}

export interface ChapterListResponse {
  chapters: ChapterListItem[];
}

export interface ChapterResponse {
  chapter: Chapter & { content: string };
}

// Author types (name/image are updated via auth.updateUser, not here)
export interface UpdateAuthorRequest {
  subdomain?: string;
  customDomain?: string | null;
}

export interface AuthorResponse {
  author: Author;
}

// Tier types
export type BillingPeriod = "monthly" | "yearly" | "one_time";

export interface Tier {
  id: string;
  authorId: string;
  name: string;
  slug: string;
  description?: string | null;
  priceCents: number;
  currency: string;
  billingPeriod: BillingPeriod;
  level: number;
  sortOrder: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTierRequest {
  name: string;
  slug: string;
  description?: string;
  priceCents?: number;
  currency?: string;
  billingPeriod?: BillingPeriod;
  level: number;
  sortOrder?: number;
}

export interface UpdateTierRequest {
  name?: string;
  slug?: string;
  description?: string | null;
  priceCents?: number;
  currency?: string;
  billingPeriod?: BillingPeriod;
  level?: number;
  sortOrder?: number;
  isDefault?: boolean;
}

export interface TierListResponse {
  tiers: Tier[];
}

export interface TierResponse {
  tier: Tier;
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

/**
 * Novel API methods
 */
const novels = {
  /**
   * List all novels for the authenticated author
   */
  list(): Promise<NovelListResponse> {
    return apiRequest<NovelListResponse>("/api/novels", { method: "GET" });
  },

  /**
   * Get a single novel by ID
   */
  get(id: string): Promise<NovelResponse> {
    return apiRequest<NovelResponse>(`/api/novels/${id}`, { method: "GET" });
  },

  /**
   * Create a new novel
   */
  create(data: CreateNovelRequest): Promise<NovelResponse> {
    return apiRequest<NovelResponse>("/api/novels", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing novel
   */
  update(id: string, data: UpdateNovelRequest): Promise<NovelResponse> {
    return apiRequest<NovelResponse>(`/api/novels/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a novel
   */
  delete(id: string): Promise<DeleteResponse> {
    return apiRequest<DeleteResponse>(`/api/novels/${id}`, {
      method: "DELETE",
    });
  },
};

/**
 * Chapter API methods (nested under novels)
 */
const chapters = {
  /**
   * List all chapters for a novel
   */
  list(novelId: string): Promise<ChapterListResponse> {
    return apiRequest<ChapterListResponse>(`/api/novels/${novelId}/chapters`, {
      method: "GET",
    });
  },

  /**
   * Get a single chapter with content
   */
  get(novelId: string, chapterId: string): Promise<ChapterResponse> {
    return apiRequest<ChapterResponse>(
      `/api/novels/${novelId}/chapters/${chapterId}`,
      { method: "GET" },
    );
  },

  /**
   * Create a new chapter
   */
  create(
    novelId: string,
    data: CreateChapterRequest,
  ): Promise<ChapterResponse> {
    return apiRequest<ChapterResponse>(`/api/novels/${novelId}/chapters`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing chapter
   */
  update(
    novelId: string,
    chapterId: string,
    data: UpdateChapterRequest,
  ): Promise<ChapterResponse> {
    return apiRequest<ChapterResponse>(
      `/api/novels/${novelId}/chapters/${chapterId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  },

  /**
   * Delete a chapter
   */
  delete(novelId: string, chapterId: string): Promise<DeleteResponse> {
    return apiRequest<DeleteResponse>(
      `/api/novels/${novelId}/chapters/${chapterId}`,
      { method: "DELETE" },
    );
  },
};

/**
 * Author API methods
 */
const authors = {
  /**
   * Get the current authenticated author's profile
   */
  getCurrentAuthor(): Promise<AuthorResponse> {
    return apiRequest<AuthorResponse>("/api/authors/me", { method: "GET" });
  },

  /**
   * Update the current author's profile
   */
  updateAuthor(data: UpdateAuthorRequest): Promise<AuthorResponse> {
    return apiRequest<AuthorResponse>("/api/authors/me", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
};

/**
 * Tier API methods
 */
const tiers = {
  /**
   * List all tiers for the authenticated author
   */
  list(): Promise<TierListResponse> {
    return apiRequest<TierListResponse>("/api/tiers", { method: "GET" });
  },

  /**
   * Get a single tier by ID
   */
  get(id: string): Promise<TierResponse> {
    return apiRequest<TierResponse>(`/api/tiers/${id}`, { method: "GET" });
  },

  /**
   * Create a new tier
   */
  create(data: CreateTierRequest): Promise<TierResponse> {
    return apiRequest<TierResponse>("/api/tiers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing tier
   */
  update(id: string, data: UpdateTierRequest): Promise<TierResponse> {
    return apiRequest<TierResponse>(`/api/tiers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a tier
   */
  delete(id: string): Promise<DeleteResponse> {
    return apiRequest<DeleteResponse>(`/api/tiers/${id}`, {
      method: "DELETE",
    });
  },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const api = {
  novels,
  chapters,
  authors,
  tiers,
};

export default api;
